/**
 * useCleaningAssistant — invokes the Haiku-backed Edge Function and returns
 * the grounded checklist + photo requirements + blocking issues + coaching
 * note for a given cleaning task.
 *
 * Hits POST /functions/v1/cleaning-assistant via supabase.functions.invoke,
 * which adds the user JWT automatically.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'
import { AppError }                       from '@/lib/errors/AppError'

export interface CoachingResult {
  task: {
    id:         string
    cycle_type: string
    status:     string
    started_at: string | null
  }
  room: {
    id:             string
    room_number:    string
    current_status: string
  }
  checklist_items: Array<{
    id:          string
    label:       string
    is_required: boolean
    completed:   boolean
  }>
  photo_requirements: Array<{
    category:    string
    is_required: boolean
  }>
  blocking_issues: Array<{
    id:       string
    title:    string
    severity: string
  }>
  coaching_note: string
  /**
   * Status of the AI coaching call. 'generated' is the happy path;
   * 'unavailable' means Anthropic upstream outage; 'malformed' means
   * the model responded but the JSON was unparseable. The UI surfaces
   * a distinct hint on 'unavailable' so the cleaner knows guidance is
   * degraded (CLAUDE.md ERROR 16).
   */
  coaching_status: 'generated' | 'unavailable' | 'malformed'
}

export function useCleaningAssistant(taskId: string | undefined): UseQueryResult<CoachingResult> {
  const supabase = useSupabase()

  return useQuery({
    queryKey: ['cleaning-assistant', taskId],
    enabled:  !!taskId,
    staleTime: 60 * 1000,
    retry:    1,
    queryFn: async (): Promise<CoachingResult> => {
      if (!taskId) throw new Error('useCleaningAssistant: taskId is required')

      const { data, error } = await supabase.functions.invoke<CoachingResult>('cleaning-assistant', {
        body: { cleaning_task_id: taskId, mode: 'checklist' },
      })

      if (error) {
        throw new AppError({
          code:        'CLEANING_ASSISTANT_FAILED',
          message:     error.message,
          userMessage: 'Could not load AI guidance for this task.',
          status:      500,
          cause:       error,
        })
      }
      if (!data) {
        throw new AppError({
          code:        'CLEANING_ASSISTANT_EMPTY',
          message:     'cleaning-assistant returned no data',
          userMessage: 'Could not load AI guidance for this task.',
          status:      500,
        })
      }
      return data
    },
  })
}
